import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelection } from '@cube3/state/src/redux/components/useSelection';
import { useModalActions } from '@cube3/state/src/redux/Hooks/useModalActions';

import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import Button from '@cube3/ui/src/Buttons/Button';
import {
  ExportMultiAssetsInfo,
  ExportSingleAssetInfo
} from '../prefabs/ExportAssetsInfo';
import { Divider } from '@cube3/ui/src/Surface/Divider';
import { useExportOptions } from '../hooks/useExportOptions';
import { Padding } from '../../Contracts/prefabs/Padding';
import { useExportTargetDefaults } from '../../AdminCenter/views/WorkspaceIntegrationsView/hooks/useExportTargetDefaults';
import { useDispatch } from 'react-redux';
import { queueTabs, toggleMediaqueue } from '@cube3/state/src/redux/ducks/ui';
import {
  ContentfulParameters,
  ContentfulSpace
} from '@cube3/common/model/resource-types';

import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';

import { useGenericFormState } from '@cube3/forms/src/hooks/useGenericFormState';
import { objectFromValues } from '@cube3/forms/src/utils/objectToFormValues';
import { GenericForm } from '@cube3/forms/src/forms/genericForm/GenericForm';
import { DisabledTitle } from '../prefabs/DisabledTitle';
import { validEmailaddress } from '@cube3/forms/src/utils/validators';

interface Props {
  modalContext: {
    /** selected profile from previous step */
    space: ContentfulSpace;
    /** selected connected account from previous step */
    exportTargetId: string;
    /** target type used for retrieving default values  */
    exportTargetTypeId: string;
  };
}

/**
 * Dynamically render options form
 */

export const ContentfulExportOptionsModal: React.FC<Props> = (props) => {
  const { space, exportTargetId, exportTargetTypeId } = props.modalContext;

  const { selection: initialSelection } = useSelection();
  const { current: selection } = useRef(initialSelection);
  const fields = useMemo(() => {
    return [
      selection?.length === 1
        ? {
            id: 'display_name',
            label: 'Title',
            type: 'string' as const,
            required: true
          }
        : {
            id: 'display_name',
            label: 'Title',
            type: 'custom' as const,
            disabled: false,
            component: DisabledTitle
          },
      {
        id: 'environment',
        label: 'Environment',
        type: 'radio' as const,
        options: space.environments.map((e) => ({
          ...e,
          value: e.id
        })),
        required: true
      },
      { id: 'divider', type: 'divider' },
      {
        id: 'recipients',
        label: 'Send export notifications to',
        type: 'multiselect',
        validate: validEmailaddress,
        allowAddingOfNewSuggestions: true
      }
    ] as Field[];
  }, [selection]);

  const [defaults, defaultsStatus] =
    useExportTargetDefaults(exportTargetTypeId);

  const initialValues = useMemo(() => {
    return [
      {
        id: 'display_name',
        value: selection.length === 1 ? selection[0]['display_name'] : undefined
      },
      ...Object.keys(defaults || {}).map((k) => {
        return { id: k, value: defaults[k] };
      })
    ];
  }, [selection, defaults]);

  const { fields: fieldsWithState, ...formState } = useGenericFormState({
    fields,
    initialValues
  });

  const { previousModal, closeAllModals, goBackModals } = useModalActions();
  const { createExportStatus, handleCreateExport, exportBatchId } =
    useExportOptions<ContentfulParameters>({
      selection,
      exportTargetId
    });

  const handleSubmit = useCallback(() => {
    const { values } = formState;
    if (!values) return;
    const formValue = objectFromValues(values);

    handleCreateExport({
      recipients: formValue.recipients as string[],
      integration_parameters: {
        display_name: formValue.display_name as string,
        environment: formValue.environment as string,
        space: space.id
      }
    });
  }, [handleCreateExport, formState, space]);

  const dispatch = useDispatch();

  // open export status view modal
  useEffect(() => {
    if (exportBatchId && createExportStatus === statuses.SUCCESS) {
      closeAllModals();
      dispatch(toggleMediaqueue(true, queueTabs.EXPORTS));
    }
  }, [
    space,
    createExportStatus,
    exportBatchId,
    formState,
    closeAllModals,
    dispatch
  ]);

  const handleCancel = useCallback(() => {
    goBackModals(2);
  }, [goBackModals]);

  return (
    <ModalMenuUI
      title="Export to Contenful • Options"
      wide={true}
      multiplyContentSpace={10}
      loading={
        defaultsStatus !== statuses.SUCCESS ||
        (createExportStatus &&
          createExportStatus !== statuses.SUCCESS &&
          createExportStatus !== statuses.FAILED)
      }
      onCloseEvent={handleCancel}
      footerLeftComponent={
        <Button
          text={'Previous'}
          onClick={previousModal}
          colorVariant={'ghost2'}
        />
      }
      footerRightComponent={
        <>
          <Button
            text={'Cancel'}
            colorVariant={'ghost2'}
            onClick={handleCancel}
            disabled={false}
          />
          <Button
            disabled={!formState?.valid}
            colorVariant={'filled1'}
            text={'Export'}
            onClick={handleSubmit}
          />
        </>
      }
    >
      <Padding>
        {selection.length > 0 ? (
          <>
            {selection.length > 1 ? (
              <ExportMultiAssetsInfo selection={selection} />
            ) : (
              <ExportSingleAssetInfo selection={selection?.[0]} />
            )}
            <Divider gutter={true} />

            <Padding>
              {hasResolved(defaultsStatus) && initialValues && (
                <GenericForm
                  form="contentfull_export_form"
                  fields={fieldsWithState}
                />
              )}
            </Padding>
          </>
        ) : null}
      </Padding>
    </ModalMenuUI>
  );
};
